import {
  StatusProjectEnum,
  StudioCourseConfigStateEnum,
  TrackModalityEnum,
} from '@/generated/graphql/types';
import {
  StatusProjectEnum as LegacyStatusProjectEnum,
  StudioCourseConfigStateEnum as LegacyStudioCourseConfigStateEnum,
} from '@/shared/types/graphql/globals.v5';

import type { GetDetailTrackByIdQueryQuery } from '../graphql/GetDetailTrackByIdQuery.v5.generated';
import type { DetailTrackType, STATUS, TCoursesTrack } from '../types';

const STATUS_PROJECT_ENUM_MAP: Record<
  StatusProjectEnum,
  LegacyStatusProjectEnum
> = {
  [StatusProjectEnum.Certified]: LegacyStatusProjectEnum.CERTIFIED,
  [StatusProjectEnum.Draft]: LegacyStatusProjectEnum.DRAFT,
  [StatusProjectEnum.Finished]: LegacyStatusProjectEnum.FINISHED,
  [StatusProjectEnum.Observed]: LegacyStatusProjectEnum.OBSERVED,
  [StatusProjectEnum.Published]: LegacyStatusProjectEnum.PUBLISHED,
  [StatusProjectEnum.Resolved]: LegacyStatusProjectEnum.RESOLVED,
};

const VISIBILITY_STATE_ENUM_MAP: Record<
  StudioCourseConfigStateEnum,
  LegacyStudioCourseConfigStateEnum
> = {
  [StudioCourseConfigStateEnum.Hidden]:
    LegacyStudioCourseConfigStateEnum.HIDDEN,
};

type TUserOrganizationTeam = NonNullable<
  GetDetailTrackByIdQueryQuery['content_organization_team']['user_organization_team']
>;
type TTeamCourse = TUserOrganizationTeam['team_courses'][number];

const parseDataListCourses = (
  listDataCourses: TTeamCourse[],
): TCoursesTrack[] => {
  return listDataCourses
    .sort((a, b) => (a?.order || 0) - (b?.order || 0))
    .map(item => {
      const visibilityState =
        item.course?.studio_course_config?.visibility_state;

      return {
        courseId: Number(item?.course_id),
        courseImage: item?.course?.image || '',
        courseName: item?.course?.title || '',
        profesorFullName: item?.course?.professor?.full_name || '',
        duration: Number(item?.course?.duration),
        progress: Number(item?.course?.progress) || 0,
        courseTypeEnum: item?.course?.course_type || '',
        lastVideoLectureUrl:
          item?.course?.first_video_lecture_url?.relative_url || '',
        hasQuiz: Boolean(item?.course_user_summary?.has_quiz),
        hasProject: Boolean(item?.course_user_summary?.has_project),
        isRequired: Boolean(item?.is_required) || false,
        trailerSource: item?.course?.trailer_source
          ? [
              {
                videoUrl: item.course.trailer_source?.video_url || '',
                videoSourceType:
                  item?.course.trailer_source?.video_source_type || '',
              },
            ]
          : [],
        order: Number(item?.order),
        typeCourse: 'course',
        visibilityState: visibilityState
          ? VISIBILITY_STATE_ENUM_MAP[visibilityState]
          : visibilityState,
        assignedAt: item?.assigned_at || null,
      };
    });
};

const parseDataDetailTrack = (
  data: TUserOrganizationTeam | null | undefined,
  numberCoinPlayroom: number,
): DetailTrackType | null => {
  if (data) {
    return {
      trackId: data?.team_id,
      trackName: data?.team_name || '',
      trackDescription: data?.description || '',
      trackImage: '',
      trackEndDate: data?.expiration_date || '',
      isCompletedTrack: data?.user_organization_team_summary?.is_completed,
      trackDisabled: data?.organization_team?.is_hidden,
      trackPlayroomCoin: numberCoinPlayroom,
      trackReward: {
        rewardId: Number(data?.organization_team?.reward?.id),
        userRewardId: Number(data?.user_reward?.id),
        numberCourses: data?.organization_team?.reward?.number_courses || 0,
        status: data?.user_reward?.status as STATUS,
      },
      trackSummary: {
        mandatoryProjectsCount:
          data?.user_organization_team_summary?.required_projects || 0,
        mandatoryCoursesCount:
          data?.user_organization_team_summary?.required_courses || 0,
        mandatoryQuizzesCount:
          data?.user_organization_team_summary?.required_quiz_courses || 0,
        inProgressCourses:
          data?.user_organization_team_summary?.completed_courses || 0,
        inProgressMandatoryProjectsCount:
          data?.user_organization_team_summary?.created_projects || 0,
        inProgressQuizzesCount:
          data?.user_organization_team_summary?.completed_quiz_courses || 0,
        totalCoursesCount:
          data?.user_organization_team_summary?.total_courses || 0,
      },
      lastProject: data?.in_progress_project
        ? {
            originalId: Number(data?.in_progress_project?.id),
            title: data?.in_progress_project?.title || '',
            url: {
              relativeUrl: data?.in_progress_project?.url?.relative_url || '',
            },
            currentActionState: data?.in_progress_project?.current_action_state
              ? STATUS_PROJECT_ENUM_MAP[
                  data.in_progress_project.current_action_state
                ]
              : null,
            projectImageThumbnailUrl:
              data?.in_progress_project?.project_image || '',
          }
        : null,
      listCourses: parseDataListCourses(data?.team_courses),
      dataCertificate: {
        attachmentUrl: data?.certificate?.attachment_url || '',
        image: data?.certificate?.image || '',
      },
      hasCertificate: data?.organization_team?.generate_certificate || false,
      trackVersion: data?.organization_team?.version || null,
      contentUpdatedAt: data?.content_updated_at || null,
      prereqsUpdatedAt: data?.prereqs_updated_at || null,
      prereqBannerType: data?.prerequisite_change?.banner_type || null,
      isClosed: Boolean(data?.last_closure),
      closedAt: data?.last_closure?.closed_at || null,
      isOpenModality:
        data?.organization_team?.modality === TrackModalityEnum.Open,
    };
  }

  return null;
};

export default parseDataDetailTrack;
